import { memo } from 'react';
import Select, { DropdownIndicatorProps, GroupBase, Props, Theme, components } from 'react-select';
import SelectStyles from '~/css/react-select';
import { useTranslate } from '~/i18n';
import { InputLayout } from './input-layout';
import cn from 'classnames';
import { FieldError } from 'react-hook-form';
import { ChevronDownIcon } from '~/svgs';

interface UISelectProps extends Props<any, boolean> {
   innerRef?: any;
   error?: FieldError | any;
   valid?: React.ReactNode;
   label?: React.ReactNode;
   labelClassName?: string;
   description?: React.ReactNode;
   groupClassName?: string;
   isRequired?: boolean;
   helperText?: React.ReactNode;
}

const DropdownIndicator = (props: DropdownIndicatorProps<unknown, boolean, GroupBase<unknown>>) => {
   return (
      components.DropdownIndicator && (
         <components.DropdownIndicator {...props}>
            <ChevronDownIcon />
         </components.DropdownIndicator>
      )
   );
};

const UISelect: React.FC<UISelectProps> = ({
   id,
   label,
   labelClassName,
   innerRef,
   valid,
   error,
   styles,
   description,
   groupClassName,
   isRequired,
   components,
   isLoading = false,
   helperText,
   ...rest
}) => {
   const { translate } = useTranslate();
   const selectTheme = (theme: Theme) => ({
      ...theme,
      spacing: {
         ...theme.spacing,
      },
      colors: {
         ...theme.colors,
         text: 'green',
         primary: error
            ? SelectStyles.errorColor
            : valid
            ? SelectStyles.successColor
            : SelectStyles.borderColors,
         primary75: SelectStyles.optionFocusedBackgroundColor,
         primary50: SelectStyles.optionFocusedBackgroundColor,
         primary25: SelectStyles.optionBackgroundColor,
         neutral0: SelectStyles.backgroundColor,
         neutral5: '#fff',
         neutral10: 'transparent',
         neutral20: error
            ? SelectStyles.errorColor
            : valid
            ? SelectStyles.successColor
            : SelectStyles.defaultBorderColors,
         neutral30: error
            ? SelectStyles.errorColor
            : valid
            ? SelectStyles.successColor
            : SelectStyles.borderHoverColor,
         neutral40: SelectStyles.noOptionsColor,
         neutral50: SelectStyles.placeholderColor,
         neutral60: SelectStyles.focusedArrowColor,
         neutral70: '#fff',
         neutral80: SelectStyles.searchColor,
         neutral90: '#fff',
      },
   });

   const selectStyles: any = {
      ...styles,
      control: (style: any, state: any) => ({
         ...style,
         height: 48,
         borderRadius: SelectStyles.borderRadius,
         backgroundColor: state.isDisabled
            ? SelectStyles.disabledBackgroundColor
            : SelectStyles.backgroundColor,
         borderColor: state.isFocused
            ? SelectStyles.borderHoverColor
            : error
            ? SelectStyles.errorColor
            : '#E6E6E6',
         boxShadow: state.isFocused ? '0 0 0 0.14rem rgba(var(--primary), 0.25)' : '',
      }),
      option: (style: any, state: any) => ({
         ...style,
         color: SelectStyles.searchColor,
         backgroundColor: state.isFocused
            ? SelectStyles.optionFocusedBackgroundColor
            : style.backgroundColor,
         '&:hover': {
            backgroundColor: SelectStyles.optionFocusedBackgroundColor,
         },
         '&:active': {
            backgroundColor: SelectStyles.optionFocusedBackgroundColor,
         },
      }),
      // input: () => ({
      //    outline: 'none',
      //    border: 'none',
      //    boxShadow: 'none',
      // }),
      dropdownIndicator: (style: any) => ({
         ...style,
         color: SelectStyles.searchColor,
      }),
      multiValue: (style: any) => ({
         ...style,
         // display: 'none',
         backgroundColor: '#646EC8',
         borderRadius: '20px',
      }),
      multiValueLabel: (style: any) => ({
         ...style,
         color: 'white',
      }),
      multiValueRemove: (style: any) => ({
         ...style,
         color: 'white',
         borderRadius: '100%',
         margin: '2px',
         ':hover': {
            backgroundColor: 'white',
            color: '#646EC8',
         },
      }),
   };

   return (
      <InputLayout
         id={id}
         label={label}
         labelClassName={labelClassName}
         groupClassName={cn(groupClassName, 'react-select')}
         description={description}
         error={error}
         isRequired={isRequired}
         valid={valid}
         helperText={helperText}>
         <Select
            components={{
               DropdownIndicator,
               IndicatorSeparator: () => null,
               ...components,
            }}
            className="react-select"
            inputId={id}
            instanceId={id}
            {...rest}
            styles={selectStyles}
            classNamePrefix="react-select"
            noOptionsMessage={message =>
               `${translate({ id: 'common:no results' })} ${message.inputValue}`
            }
            ref={innerRef}
            theme={selectTheme}
            isSearchable={true}
            isLoading={isLoading}
         />
      </InputLayout>
   );
};

export default memo(UISelect);
